import {
  type DocumentData,
  collection,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { fromFirestorePodcastEpisode } from '@core/services/podcasts/converters/podcast-episode-converter'
import { PodcastsErrorConverter } from '@core/services/podcasts/converters/podcasts-error-converter'
import { fromFirestorePodcastShow } from '@core/services/podcasts/converters/podcast-show-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { PodcastEpisode, PodcastShow } from '@domain/models/podcast'

const PODCAST_SHOWS_COLLECTION = 'podcast_shows'
const PODCAST_EPISODES_COLLECTION = 'podcast_episodes'

export type PodcastsFetchPolicy = 'default' | 'server'

export interface PaginatedPodcastEpisodesResult {
  episodes: PodcastEpisode[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

function getPodcastShowsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return collection(firestore, PODCAST_SHOWS_COLLECTION)
}

function getPodcastEpisodesCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return collection(firestore, PODCAST_EPISODES_COLLECTION)
}

function getPodcastShowDoc(showId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return doc(firestore, PODCAST_SHOWS_COLLECTION, showId)
}

function getPodcastEpisodeDoc(episodeId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return doc(firestore, PODCAST_EPISODES_COLLECTION, episodeId)
}

function buildActiveEpisodesQuery(
  showId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
) {
  const constraints: QueryConstraint[] = [
    where('showId', '==', showId),
    where('isActive', '==', true),
    orderBy('releaseDate', 'desc'),
  ]

  if (lastDocument) {
    constraints.push(startAfter(lastDocument))
  }

  constraints.push(limit(pageSize))
  return query(getPodcastEpisodesCollection(), ...constraints)
}

async function getActiveEpisodesSnapshot(
  showId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  fetchPolicy: PodcastsFetchPolicy = 'default',
) {
  const queryRef = buildActiveEpisodesQuery(showId, pageSize, lastDocument)
  if (fetchPolicy === 'server') {
    return getDocsFromServer(queryRef)
  }
  return getDocs(queryRef)
}

export class PodcastsService {
  private static instance: PodcastsService

  private constructor() {}

  public static getInstance(): PodcastsService {
    if (!PodcastsService.instance) {
      PodcastsService.instance = new PodcastsService()
    }
    return PodcastsService.instance
  }

  public async fetchPodcastShow(showId: string): Promise<PodcastShow> {
    try {
      const directDoc = await getDoc(getPodcastShowDoc(showId))
      if (directDoc.exists()) {
        return fromFirestorePodcastShow(directDoc)
      }

      const fallbackQuery = query(
        getPodcastShowsCollection(),
        where('showId', '==', showId),
        limit(1),
      )
      const fallbackSnapshot = await getDocs(fallbackQuery)
      const fallbackDoc = fallbackSnapshot.docs[0]
      if (fallbackDoc) {
        return fromFirestorePodcastShow(fallbackDoc)
      }

      throw new CoreError({
        cause: CoreErrorCause.DataNotFound,
        context: `Podcast show "${showId}" was not found.`,
      })
    } catch (error) {
      throw PodcastsErrorConverter.domainModel(error)
    }
  }

  public async fetchPodcastEpisode(episodeId: string): Promise<PodcastEpisode> {
    try {
      const snapshot = await getDoc(getPodcastEpisodeDoc(episodeId))
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Podcast episode "${episodeId}" was not found.`,
        })
      }
      return fromFirestorePodcastEpisode(snapshot)
    } catch (error) {
      throw PodcastsErrorConverter.domainModel(error)
    }
  }

  public async fetchLatestPodcastEpisodes(
    showId: string,
    count = 3,
    fetchPolicy: PodcastsFetchPolicy = 'default',
  ): Promise<PodcastEpisode[]> {
    try {
      const safeLimit = count > 0 ? count : 3
      const snapshot = await getActiveEpisodesSnapshot(
        showId,
        safeLimit,
        null,
        fetchPolicy,
      )
      return snapshot.docs.map((item) => fromFirestorePodcastEpisode(item))
    } catch (error) {
      throw PodcastsErrorConverter.domainModel(error)
    }
  }

  public async fetchPodcastEpisodesPaginated(
    showId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
    fetchPolicy: PodcastsFetchPolicy = 'default',
  ): Promise<PaginatedPodcastEpisodesResult> {
    try {
      const safePageSize = pageSize > 0 ? pageSize : 12
      const snapshot = await getActiveEpisodesSnapshot(
        showId,
        safePageSize,
        lastDocument,
        fetchPolicy,
      )
      const episodes = snapshot.docs.map((item) => fromFirestorePodcastEpisode(item))
      const lastDoc =
        snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null

      return {
        episodes,
        lastDoc,
        hasMore: snapshot.docs.length >= safePageSize,
      }
    } catch (error) {
      throw PodcastsErrorConverter.domainModel(error)
    }
  }
}

const podcastsService = PodcastsService.getInstance()

export async function fetchPodcastShow(showId: string): Promise<PodcastShow> {
  return podcastsService.fetchPodcastShow(showId)
}

export async function fetchPodcastEpisode(
  episodeId: string,
): Promise<PodcastEpisode> {
  return podcastsService.fetchPodcastEpisode(episodeId)
}

export async function fetchLatestPodcastEpisodes(
  showId: string,
  count = 3,
  fetchPolicy: PodcastsFetchPolicy = 'default',
): Promise<PodcastEpisode[]> {
  return podcastsService.fetchLatestPodcastEpisodes(showId, count, fetchPolicy)
}

export async function fetchPodcastEpisodesPaginated(
  showId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  fetchPolicy: PodcastsFetchPolicy = 'default',
): Promise<PaginatedPodcastEpisodesResult> {
  return podcastsService.fetchPodcastEpisodesPaginated(
    showId,
    pageSize,
    lastDocument,
    fetchPolicy,
  )
}
